import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Services', path: '#', hasDropdown: true },
    { name: 'Areas', path: '/areas-we-cover' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'About', path: '/about' },
    { name: 'FAQs', path: '/faqs' },
  ];

  const serviceLinks = [
    { name: 'House Removals', path: '/house-removals' },
    { name: 'Office Removals', path: '/office-removals' },
    { name: 'Single Item Moves', path: '/single-item-moves' },
    { name: 'Collections & Deliveries', path: '/collections-deliveries' },
  ];

  const [showServicesDropdown, setShowServicesDropdown] = useState(false);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <nav className="mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="font-sans font-bold cursor-pointer">
              <div className={`text-2xl sm:text-3xl leading-tight text-center transition-colors ${
                isScrolled || location.pathname !== '/' ? 'text-charcoal' : 'text-white'
              }`}>Dan & Van</div>
              <div className="text-[10px] sm:text-xs tracking-[0.2em] font-medium text-gold text-center">REMOVALS</div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => link.hasDropdown && setShowServicesDropdown(true)}
                  onMouseLeave={() => link.hasDropdown && setShowServicesDropdown(false)}
                >
                  {link.hasDropdown ? (
                    <>
                      <button className={`font-sans text-[15px] hover:text-gold transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                        isScrolled || location.pathname !== '/' ? 'text-charcoal' : 'text-white'
                      }`}>
                        {link.name}
                        <i className="ri-arrow-down-s-line ml-1"></i>
                      </button>

                      {/* Services Dropdown - Fixed positioning */}
                      {showServicesDropdown && (
                        <div className="absolute top-full left-0 pt-2">
                          <div className="w-56 bg-white shadow-luxury rounded-md py-2">
                            {serviceLinks.map((service) => (
                              <Link
                                key={service.name}
                                to={service.path}
                                className="block px-4 py-2 text-sm text-charcoal hover:bg-gray-50 hover:text-gold transition-colors whitespace-nowrap"
                              >
                                {service.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={link.path}
                      className={`font-sans text-[15px] hover:text-gold transition-colors duration-200 relative group whitespace-nowrap ${
                        isScrolled || location.pathname !== '/' ? 'text-charcoal' : 'text-white'
                      }`}
                    >
                      {link.name}
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gold transition-all duration-200 group-hover:w-full"></span>
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center space-x-4">
              <a
                href="tel:07530166035"
                className={`px-6 py-3 border-2 font-sans text-[15px] font-medium rounded transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  isScrolled || location.pathname !== '/' 
                    ? 'border-charcoal text-charcoal hover:border-gold hover:text-gold' 
                    : 'border-white text-white hover:border-gold hover:text-gold'
                }`}
              >
                Call 07530 166035
              </a>
              <Link
                to="/contact"
                className="px-6 py-3 bg-gold text-charcoal font-sans text-[15px] font-semibold rounded hover:shadow-luxury-hover transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Get a Quote
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center space-x-3">
              <Link
                to="/contact"
                className="px-4 py-2 bg-gold text-charcoal font-sans text-sm font-semibold rounded whitespace-nowrap cursor-pointer"
              >
                Quote
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`transition-colors duration-300 cursor-pointer ${
                  isScrolled || location.pathname !== '/' ? 'text-charcoal' : 'text-white'
                }`}
                aria-label="Toggle menu"
              >
                <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed top-0 right-0 bottom-0 w-80 bg-white z-50 lg:hidden overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-8">
                  <span className="font-sans text-2xl font-bold text-charcoal">Menu</span>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-charcoal cursor-pointer"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <div className="font-sans text-xs uppercase tracking-wider text-gold mb-3">
                      Services
                    </div>
                    {serviceLinks.map((service) => (
                      <Link
                        key={service.name}
                        to={service.path}
                        className="block py-2 text-charcoal hover:text-gold transition-colors"
                      >
                        {service.name}
                      </Link>
                    ))}
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    {navLinks.filter(link => !link.hasDropdown).map((link) => (
                      <Link
                        key={link.name}
                        to={link.path}
                        className="block py-3 text-charcoal hover:text-gold transition-colors"
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <a
                      href="tel:07530166035"
                      className="block w-full py-3 text-center border-2 border-charcoal text-charcoal font-medium rounded hover:border-gold hover:text-gold transition-all mb-3 whitespace-nowrap cursor-pointer"
                    >
                      Call 07530 166035
                    </a>
                    <Link
                      to="/contact"
                      className="block w-full py-3 text-center bg-gold text-charcoal font-semibold rounded whitespace-nowrap cursor-pointer"
                    >
                      Get a Quote
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
